import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { NotificationsProvider } from "./lib/NotificationsContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ProductDetail from "./pages/ProductDetail";
import ProductResearch from "./pages/ProductResearch";
import ProductResearchDetail from "./pages/ProductResearchDetail";
import WinningProducts from "./pages/WinningProducts";
import WinningProductDetail from "./pages/WinningProductDetail";
import AdsPage from "./pages/AdsPage";
import UGCAdDetail from "./pages/UGCAdDetail";
import StaticAdDetail from "./pages/StaticAdDetail";
import ShopifyPage from "./pages/ShopifyPage";
import ShopifyProductDetail from "./pages/ShopifyProductDetail";
import NotificationsPage from "./pages/NotificationsPage";
import ConfirmProductDetails from "./pages/ConfirmProductDetails";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/product/:ecomId" 
        element={
          <ProtectedRoute>
            <ProductDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/product-research" 
        element={
          <ProtectedRoute>
            <ProductResearch />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/product-research/:ecomId" 
        element={
          <ProtectedRoute>
            <ProductResearchDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/winning-products" 
        element={
          <ProtectedRoute>
            <WinningProducts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/winning-products/:ecomId" 
        element={
          <ProtectedRoute>
            <WinningProductDetail />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/confirm-product/:ecomId"
        element={
          <ProtectedRoute>
            <ConfirmProductDetails />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/ads" 
        element={
          <ProtectedRoute>
            <AdsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ads/ugc/:ecomId" 
        element={
          <ProtectedRoute>
            <UGCAdDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ads/static/:ecomId" 
        element={
          <ProtectedRoute>
            <StaticAdDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/shopify" 
        element={
          <ProtectedRoute>
            <ShopifyPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/shopify/:ecomId" 
        element={
          <ProtectedRoute>
            <ShopifyProductDetail />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationsProvider>
        <div className="App">
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
              },
            }}
          />
        </div>
      </NotificationsProvider>
    </AuthProvider>
  );
}

export default App;
